import { IColumn, TooltipHost } from '@fluentui/react';
import { FC, useMemo } from 'react';
import { TimeStamp } from '../../models/reports.internal';
import { HDQueueSegment, useGetAbandonedConversationsQuery } from '../../redux/apis/historicalDataApi';
import { getTimestamp } from '../../utils/timeUtils';
import usePagedListReducer from '../../shared/hooks/usePagedListReducer';
import useTimeFormatters from '../../shared/hooks/useTimeFormatters';
import PagedDetailsList from './PagedDetailsList';
import { TooltipWrapper } from './TooltipWrapper';

interface IProps {
    queueId: string;
    startOfToday: TimeStamp | null;
}

const AbandonedConversations: FC<IProps> = ({ queueId, startOfToday }) => {
    const [{ page, pageSize, sortingColumn, isSortedDescending }, dispatch] = usePagedListReducer({ sortingColumn: 'queuedAt' });
    const { formatDateTime, formatTimeSpan } = useTimeFormatters();

    const lastMidnight = useMemo(
        () => startOfToday ? new Date(startOfToday * 1000).toISOString() : '',
        [startOfToday],
    );

    const { data: results, isLoading } = useGetAbandonedConversationsQuery(
        {
            queueId,
            from: lastMidnight ?? '',
            skip: page * pageSize,
            take: pageSize,
            orderBy: `${sortingColumn} ${isSortedDescending ? 'desc' : 'asc'}`,
        },
        {
            skip: !lastMidnight,
            refetchOnMountOrArgChange: true,
        },
    );

    const columns = useMemo(
        (): IColumn[] => [
            {
                key: 'timestamp',
                name: 'Timestamp',
                minWidth: 150,
                maxWidth: 150,
                onRender: ({ queuedAtUtc }: HDQueueSegment) => <TooltipWrapper text={formatDateTime(getTimestamp(queuedAtUtc))} />,
                onColumnClick: () => dispatch({ type: 'ApplySorting', value: 'queuedAt' }),
                isSorted: sortingColumn === 'queuedAt',
                isSortedDescending,
            },
            {
                key: 'waitTime',
                name: 'Wait time',
                minWidth: 80,
                maxWidth: 80,
                onRender: ({ durationInSeconds }: HDQueueSegment) => {
                    const timeStr = formatTimeSpan(durationInSeconds);
                    return <TooltipHost content={timeStr}>{timeStr}</TooltipHost>;
                },
                onColumnClick: () => dispatch({ type: 'ApplySorting', value: 'durationInSeconds' }),
                isSorted: sortingColumn === 'durationInSeconds',
                isSortedDescending,
            },
            {
                key: 'customerName',
                name: 'Customer name',
                minWidth: 150,
                onRender: ({ conversation }: HDQueueSegment) => {
                    const name = [conversation.customerFirstName, conversation.customerMiddleName, conversation.customerLastName].filter(n => n !== '').join(' ') || 'Unknown';
                    return <TooltipHost content={name}>{name}</TooltipHost>;
                },
            },
            {
                key: 'customerIdentifier',
                name: 'Customer identifier',
                minWidth: 150,
                maxWidth: 150,
                onRender: ({ conversation }: HDQueueSegment) => {
                    const identifier = conversation.customerPhoneNumber || conversation.customerEmailAddress || '';
                    return <TooltipHost content={identifier}>{identifier}</TooltipHost>;
                },
            },
        ],
        [dispatch, formatDateTime, formatTimeSpan, isSortedDescending, sortingColumn],
    );

    const abandonedConversations = useMemo<HDQueueSegment[]>(
        () => results?.queueSegments ?? [],
        [results],
    );

    const total = results?.total ?? 0;

    return (
        <PagedDetailsList
            total={total}
            items={abandonedConversations}
            columns={columns}
            isLoading={isLoading}

            pageIndex={page}
            onPrevPageClick={() => dispatch({ type: 'PrevPage' })}
            onNextPageClick={() => dispatch({ type: 'NextPage' })}

            allowSearch={false}
            allowFilter={false}
            allowPageSizeChange={true}
            onPageSizeChange={(value) => dispatch({ type: 'SetPageSize', value })}

            styles={{ root: { minWidth: '960px !important', height: '600px !important' } }}
        />
    );
};
export default AbandonedConversations;